// src/pages/Admin_Module.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  Building2,
  Save as SaveIcon,
  User as UserIcon,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com";

// The catalog of modules you listed (label + link)
const MODULE_CATALOG = [

  
  { label: "Route Management",        link: "route.php", id : 3 },
  { label: "Guard Management",        link: "guard-management.php", id : 4 },
  { label: "Mobile Patrol",           link: "mobile-patrol.php", id : 5 },
  { label: "QR Code Generation",      link: "qrcode.php", id : 6 },
  { label: "Reporting",               link: "reporting.php", id : 7 },
  { label: "Settings",                link: "settings.php", id : 8 },
  { label: "Customization",           link: "customization.php", id : 9 },
  { label: "PWA Features",            link: "pwa-features.php", id : 10 },
  { label: "RealTime Module",         link: "real-time.php", id : 11 },
  { label: "Payroll API Integration", link: "payrollapi.php", id : 12 },
  { label: "Security Consideration",  link: "security.php", id : 14 },
];

function useTenantId() {
  const q = new URLSearchParams(useLocation().search);
  return q.get("tid");
}

export default function Admin_Module() {
  const { username: routeUsername } = useParams();
  const tenantId = useTenantId();
  const username = decodeURIComponent(routeUsername || "");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tenant, setTenant] = useState(null); // {tenantCode, databaseName, company_name?}
  const [checked, setChecked] = useState({}); // { "route.php": true, ... }

  const allChecked = useMemo(
    () =>
      MODULE_CATALOG.length > 0 &&
      MODULE_CATALOG.every(m => checked[m.link]),
    [checked]
  );

  useEffect(() => {
    (async () => {
      if (!tenantId || !username) return;
      setLoading(true);
      try {
        // 1) load tenant + current grants
        const [t, g] = await Promise.all([
          axios.get(`${API}/api/tenant/basic/${tenantId}`),          // small helper (step 3 adds it)
          axios.get(`${API}/api/tenant/mysql/modules/${tenantId}/${encodeURIComponent(username)}`),
        ]);

        setTenant(t.data?.tenant || null);

        // normalize grants -> checked map by link
        const grants = g.data?.modules || [];
        const map = {};
        grants.forEach(row => { if (row.approve) map[row.links] = true; });
        setChecked(map);
      } catch (e) {
        Swal.fire("Error", e?.response?.data?.error || e.message, "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, username]);

  const toggleAll = () => {
    const next = {};
    if (!allChecked) MODULE_CATALOG.forEach(m => (next[m.link] = true));
    setChecked(next);
  };

  const toggleOne = (link) => {
    setChecked(prev => ({ ...prev, [link]: !prev[link] }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!tenantId || !username) return;

    const selected = MODULE_CATALOG
    .filter(m => checked[m.link])
    .map(m => ({
      pg_id: m.id,                 // 👈 send page id
      modules: m.label,
      links: m.link,
      approve: 1,
    }));

    setSaving(true);
    try {
      await axios.put(
        `${API}/api/tenant/mysql/modules/${tenantId}/${encodeURIComponent(username)}`,
        { modules: selected }
      );
      Swal.fire("Saved", "Module access updated.", "success");
      navigate("/superAdmin/admin_Menu");
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Boxes className="h-5 w-5" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Assign Modules
                    </h1>
                    <p className="text-white/80 text-sm">
                      Enable/Disable menus for this admin user
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to="/superAdmin/admin_Menu"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-white ring-1 ring-white/30 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Link>
                  <div className="text-sm text-white/90">
                    <Time />
                  </div>
                </div>
              </div>

              {/* Tenant + User pills */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Building2 className="h-4 w-4" />
                  {tenant?.tenantCode || "Tenant"} — {tenant?.databaseName || "-"}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <UserIcon className="h-4 w-4" />
                  {username}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <BadgeCheck className="h-4 w-4" />
                  Select to grant access
                </span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <form onSubmit={onSave} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-cyan-600" />
                  <h3 className="text-sm font-semibold text-slate-800">Modules</h3>
                </div>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="rounded-lg bg-slate-900 text-white px-3 py-1.5 text-sm shadow hover:bg-black"
                  disabled={loading}
                >
                  {allChecked ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {MODULE_CATALOG.map(m => (
                  <label
                    key={m.link}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!checked[m.link]}
                      onChange={() => toggleOne(m.link)}
                      disabled={loading}
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-800">{m.label}</div>
                      <div className="text-xs text-slate-500">{m.link}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-end gap-2 px-4 py-4 border-t border-slate-200 rounded-b-2xl bg-white/60">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                  disabled={saving || loading}
                >
                  <SaveIcon className="h-4 w-4" />
                  {saving ? "Saving…" : "Save Modules"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
